import React, { useMemo, useState } from "react";
import { fmtMoney, fmtNumber } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Car, TrendingDown, Info, FileText, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";
import NudgeCard from "@/components/NudgeCard";
import {
  IRD_KM_RATES,
  IRD_KM_TIER_THRESHOLD,
  GST_RATE,
  MIN_WAGE_HOURLY,
  GIG_FLAT_RATE,
  TAX_YEAR,
  incomeTax,
  marginalTaxOn,
  irdKmDeduction,
} from "@/lib/nzTax";

// ---------- Fuel-type labels for the selector ---------------------------
const FUEL_OPTIONS = [
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "petrol_hybrid", label: "Petrol hybrid" },
  { value: "ev", label: "EV (electric)" },
];

const PLATFORM_OPTIONS = [
  { value: "uber", label: "Uber (rides)" },
  { value: "uber_eats", label: "Uber Eats (delivery)" },
  { value: "generic", label: "Other gig driving" },
];

// ---------- Compute ----------------------------------------------------
function compute(inputs) {
  const mainIncome = Number(inputs.mainIncome) || 0;
  const hoursPerWeek = Number(inputs.hoursPerWeek) || 0;
  const weeksPerYear = Number(inputs.weeksPerYear) || 0;
  const hourlyGross = Number(inputs.hourlyGross) || 0;
  const kmPerHour = Number(inputs.kmPerHour) || 0;
  const totalKmPerYear = Number(inputs.totalKmPerYear) || 0;

  const annualHours = hoursPerWeek * weeksPerYear;
  const grossDrivingIncome = hourlyGross * annualHours;
  const businessKm = kmPerHour * annualHours;

  // ---- Vehicle costs ----
  let vehicleCosts = 0;
  let vehicleBreakdown = null;
  if (inputs.expenseMethod === "actual") {
    // Actual-cost method: sum annual costs then pro-rate to business use
    const annualActual =
      (Number(inputs.actualFuel) || 0) +
      (Number(inputs.actualInsurance) || 0) +
      (Number(inputs.actualRegoWof) || 0) +
      (Number(inputs.actualTyres) || 0) +
      (Number(inputs.actualServicing) || 0) +
      (Number(inputs.actualDepreciation) || 0);
    // If they gave total km, business fraction = businessKm / totalKm.
    // If they didn't, assume the annual figure is already business-only.
    const totalKm = totalKmPerYear > 0 ? totalKmPerYear : businessKm;
    const businessFraction = totalKm > 0 ? Math.min(1, businessKm / totalKm) : 1;
    vehicleCosts = annualActual * businessFraction;
    vehicleBreakdown = { method: "actual", businessFraction, annualActual };
  } else {
    const d = irdKmDeduction(businessKm, totalKmPerYear || businessKm, inputs.fuelType);
    vehicleCosts = d.total;
    vehicleBreakdown = { method: "ird", ...d };
  }

  // ---- GST effect ----
  // Non-registered drivers: Uber remits 6.5% GST for you and credits 8.5%
  //   back — payout already reflects it, no adjustment needed here.
  // GST-registered drivers: 15% of fares is owed to IRD; approx 15% of
  //   vehicle costs can be claimed back.
  let gstNet = 0;
  if (inputs.gstRegistered) {
    const gstOnFares = grossDrivingIncome * (GST_RATE / (1 + GST_RATE));
    const gstOnCosts = vehicleCosts * (GST_RATE / (1 + GST_RATE));
    gstNet = gstOnFares - gstOnCosts; // net cash paid to IRD
  }

  // ---- Taxable profit ----
  // For GST-registered drivers, income & expenses are treated ex-GST for
  // income-tax purposes. We approximate by removing the GST portion from
  // both sides.
  const gstFactor = inputs.gstRegistered ? 1 / (1 + GST_RATE) : 1;
  const taxableIncome = grossDrivingIncome * gstFactor;
  const taxableCosts = vehicleCosts * gstFactor;
  const taxableProfit = Math.max(0, taxableIncome - taxableCosts);

  // ---- Marginal income tax + ACC on that profit ----
  const marginal = marginalTaxOn(taxableProfit, mainIncome);

  // ---- Net in pocket ----
  const netInPocket = grossDrivingIncome - vehicleCosts - marginal.tax - marginal.acc - gstNet;
  const netHourly = annualHours > 0 ? netInPocket / annualHours : 0;

  // ---- Comparisons ----
  // Main-job net hourly (assume 40h × 52w = 2080 standard hours)
  const mainTax = incomeTax(mainIncome);
  const mainNet = mainIncome - mainTax; // rough — excludes their own ACC for simplicity
  const mainHourly = mainIncome > 0 ? mainNet / 2080 : 0;
  const altHourly = Number(inputs.alternativeHourly) || mainHourly || 0;

  // ---- Sensitivity: fuel prices +20% ----
  let netHourlyIfFuelUp = netHourly;
  if (inputs.expenseMethod === "actual") {
    const fuelBump = (Number(inputs.actualFuel) || 0) * 0.2;
    const totalKm = totalKmPerYear > 0 ? totalKmPerYear : businessKm;
    const businessFraction = totalKm > 0 ? Math.min(1, businessKm / totalKm) : 1;
    const worseVehicle = vehicleCosts + fuelBump * businessFraction;
    const worseProfit = Math.max(0, (grossDrivingIncome - worseVehicle) * gstFactor);
    const worseMarginal = marginalTaxOn(worseProfit, mainIncome);
    const worseGstNet = inputs.gstRegistered
      ? grossDrivingIncome * (GST_RATE / (1 + GST_RATE)) -
        worseVehicle * (GST_RATE / (1 + GST_RATE))
      : 0;
    const worseNet =
      grossDrivingIncome - worseVehicle - worseMarginal.tax - worseMarginal.acc - worseGstNet;
    netHourlyIfFuelUp = annualHours > 0 ? worseNet / annualHours : 0;
  } else {
    // For IRD method, bump the km rates 20% (proxy for fuel-driven cost jump)
    const worseVehicle = vehicleCosts * 1.15; // rates aren't 100% fuel, so soften to 15%
    const worseProfit = Math.max(0, (grossDrivingIncome - worseVehicle) * gstFactor);
    const worseMarginal = marginalTaxOn(worseProfit, mainIncome);
    const worseNet =
      grossDrivingIncome - worseVehicle - worseMarginal.tax - worseMarginal.acc - gstNet;
    netHourlyIfFuelUp = annualHours > 0 ? worseNet / annualHours : 0;
  }

  // ---- Break-even: gross hourly at which net = 0 ----
  // Approx: netHourly = hourlyGross - vehiclePerHour - tax/hr - acc/hr - gst/hr
  const vehiclePerHour = annualHours > 0 ? vehicleCosts / annualHours : 0;

  return {
    // inputs echoed
    annualHours,
    businessKm,
    // gross
    grossDrivingIncome,
    hourlyGross,
    // costs
    vehicleCosts,
    vehiclePerHour,
    vehicleBreakdown,
    // tax
    marginalTax: marginal.tax,
    accLevy: marginal.acc,
    marginalRatePct: marginal.marginalRatePct,
    gstNet,
    // taxable
    taxableIncome,
    taxableCosts,
    taxableProfit,
    // outputs
    netInPocket,
    netHourly,
    netHourlyIfFuelUp,
    // comparisons
    mainHourly,
    altHourly,
    minWage: MIN_WAGE_HOURLY,
  };
}

// ---------- Component --------------------------------------------------
export default function RideshareCalc() {
  const { t } = useLang();

  const [mainIncome, setMainIncome] = useState(70000);
  const [platform, setPlatform] = useState("uber");
  const [hourlyGross, setHourlyGross] = useState(32);
  const [hoursPerWeek, setHoursPerWeek] = useState(15);
  const [weeksPerYear, setWeeksPerYear] = useState(48);
  const [kmPerHour, setKmPerHour] = useState(25);
  const [totalKmPerYear, setTotalKmPerYear] = useState(0);
  const [expenseMethod, setExpenseMethod] = useState("ird"); // ird | actual
  const [fuelType, setFuelType] = useState("petrol");
  // Default OFF: most part-time drivers stay under the $60k GST threshold.
  const [gstRegistered, setGstRegistered] = useState(false);
  const [alternativeHourly, setAlternativeHourly] = useState("");

  // Actual costs
  const [actualFuel, setActualFuel] = useState(2800);
  const [actualInsurance, setActualInsurance] = useState(900);
  const [actualRegoWof] = useState(200);
  const [actualTyres, setActualTyres] = useState(500);
  const [actualServicing, setActualServicing] = useState(600);
  const [actualDepreciation, setActualDepreciation] = useState(2000);

  // ---- Uber statement importer ----
  const [importOpen, setImportOpen] = useState(false);
  const [importedAt, setImportedAt] = useState(null);
  const [stmtIncome, setStmtIncome] = useState("11344.63");
  const [stmtKm, setStmtKm] = useState("6144");
  const [stmtTrips, setStmtTrips] = useState("1359");
  const [stmtWeeks, setStmtWeeks] = useState("52");
  const [stmtHoursWeek, setStmtHoursWeek] = useState("15");
  const [stmtUpliftPct, setStmtUpliftPct] = useState("35");

  const applyStatement = () => {
    const income = Number(stmtIncome) || 0;
    const onTripKm = Number(stmtKm) || 0;
    const weeks = Math.max(1, Number(stmtWeeks) || 52);
    const hrsWk = Math.max(0.1, Number(stmtHoursWeek) || 0);
    const upliftPct = Math.max(0, Number(stmtUpliftPct) || 0);

    const totalHours = hrsWk * weeks;
    if (totalHours <= 0) return;

    // Real business km = on-trip km × (1 + dead-miles uplift)
    const realBusinessKm = onTripKm * (1 + upliftPct / 100);
    const derivedHourlyGross = income / totalHours;
    const derivedKmPerHour = realBusinessKm / totalHours;

    // Push into the main calculator state
    setHourlyGross(Math.round(derivedHourlyGross * 100) / 100);
    setKmPerHour(Math.round(derivedKmPerHour * 10) / 10);
    setHoursPerWeek(hrsWk);
    setWeeksPerYear(weeks);
    setImportedAt(new Date());
  };

  const r = useMemo(
    () =>
      compute({
        mainIncome,
        platform,
        hourlyGross,
        hoursPerWeek,
        weeksPerYear,
        kmPerHour,
        totalKmPerYear,
        expenseMethod,
        fuelType,
        gstRegistered,
        alternativeHourly,
        actualFuel,
        actualInsurance,
        actualRegoWof,
        actualTyres,
        actualServicing,
        actualDepreciation,
      }),
    [
      mainIncome,
      platform,
      hourlyGross,
      hoursPerWeek,
      weeksPerYear,
      kmPerHour,
      totalKmPerYear,
      expenseMethod,
      fuelType,
      gstRegistered,
      alternativeHourly,
      actualFuel,
      actualInsurance,
      actualRegoWof,
      actualTyres,
      actualServicing,
      actualDepreciation,
    ]
  );

  const verdict = useMemo(() => {
    if (!r.annualHours || r.hourlyGross <= 0) {
      return { tone: "info", text: "Enter your gross hourly rate and hours to see the result." };
    }
    if (r.netHourly < 0) {
      return {
        tone: "warning",
        text: `You're losing ${fmtMoney(Math.abs(r.netHourly))}/hr once tax and car costs are counted. This is not a viable side hustle at these numbers.`,
      };
    }
    if (r.netHourly < r.minWage) {
      return {
        tone: "warning",
        text: `Your true rate of ${fmtMoney(r.netHourly)}/hr is below the NZ minimum wage of ${fmtMoney(r.minWage)}/hr.`,
      };
    }
    const alt = r.altHourly || r.mainHourly;
    if (alt > 0) {
      const delta = r.netHourly - alt;
      if (delta >= 0) {
        return {
          tone: "success",
          text: `Driving beats your alternative by ${fmtMoney(delta)}/hr after everything.`,
        };
      }
      return {
        tone: "warning",
        text: `Driving pays ${fmtMoney(Math.abs(delta))}/hr less than your alternative (${fmtMoney(alt)}/hr).`,
      };
    }
    return { tone: "info", text: `Your true after-tax, after-costs rate is ${fmtMoney(r.netHourly)}/hr.` };
  }, [r]);

  return (
    <div className="stagger-in">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 08</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">
        {t("rideshare.h1", "Is Uber Driving Worth It?")}
      </h1>
      <p className="text-zinc-400 mt-2 max-w-2xl">
        {t(
          "rideshare.sub",
          "Uber & Uber Eats hourly rate — after tax, after car costs, and stacked on top of your day job. Honest answer in one screen."
        )}
      </p>

      {/* ---- Uber statement importer (collapsible) ---- */}
      <div
        className="mt-8 border border-[var(--border)] bg-[var(--surface)]"
        data-testid="rideshare-import-panel"
      >
        <button
          onClick={() => setImportOpen((s) => !s)}
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-4 hover:bg-[var(--surface-2)] transition-colors text-left"
          data-testid="rideshare-import-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 bg-[var(--primary)]/10 border border-[var(--primary)]/30 flex items-center justify-center">
              <FileText size={16} className="text-[var(--primary)]" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-base sm:text-lg tracking-tight">
                Have your Uber tax summary handy?
              </div>
              <div className="text-[11px] font-mono text-zinc-500 mt-0.5 uppercase tracking-wider">
                Paste the numbers · we&apos;ll fill the calculator for you
              </div>
            </div>
          </div>
          {importOpen ? (
            <ChevronDown size={18} className="text-zinc-500 shrink-0" />
          ) : (
            <ChevronRight size={18} className="text-zinc-500 shrink-0" />
          )}
        </button>

        {importOpen && (
          <div className="border-t border-[var(--border)] p-4 sm:p-6 space-y-5">
            <div className="text-xs font-mono text-zinc-400 leading-relaxed max-w-2xl">
              Open your Uber tax summary (Uber sends this each financial year — you
              can also see it in the app under <em>Account → Tax info</em>). Copy the{" "}
              <span className="text-[var(--primary)]">Total Income</span> and{" "}
              <span className="text-[var(--primary)]">On Trip Mileage</span> numbers
              straight into the fields below.
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Total Income ($)</div>
                <input
                  type="number"
                  step="0.01"
                  className="input-sharp text-lg py-3"
                  value={stmtIncome}
                  onChange={(e) => setStmtIncome(e.target.value)}
                  data-testid="rideshare-stmt-income"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">On Trip Mileage (km)</div>
                <input
                  type="number"
                  step="1"
                  className="input-sharp text-lg py-3"
                  value={stmtKm}
                  onChange={(e) => setStmtKm(e.target.value)}
                  data-testid="rideshare-stmt-km"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Trips (optional)</div>
                <input
                  type="number"
                  step="1"
                  className="input-sharp text-lg py-3"
                  value={stmtTrips}
                  onChange={(e) => setStmtTrips(e.target.value)}
                  data-testid="rideshare-stmt-trips"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Weeks in period</div>
                <input
                  type="number"
                  step="1"
                  className="input-sharp text-lg py-3"
                  value={stmtWeeks}
                  onChange={(e) => setStmtWeeks(e.target.value)}
                  data-testid="rideshare-stmt-weeks"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Avg hours / week driving</div>
                <input
                  type="number"
                  step="0.5"
                  className="input-sharp text-lg py-3"
                  value={stmtHoursWeek}
                  onChange={(e) => setStmtHoursWeek(e.target.value)}
                  data-testid="rideshare-stmt-hours"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Dead-miles uplift (%)</div>
                <input
                  type="number"
                  step="5"
                  className="input-sharp text-lg py-3"
                  value={stmtUpliftPct}
                  onChange={(e) => setStmtUpliftPct(e.target.value)}
                  data-testid="rideshare-stmt-uplift"
                />
              </div>
            </div>

            <div className="text-[11px] font-mono text-zinc-500 leading-relaxed max-w-2xl">
              <strong className="text-zinc-300">Dead miles?</strong> Uber&apos;s
              &quot;On Trip Mileage&quot; only counts km with a passenger. Real
              business km is 25–50% higher once you include driving to pickups,
              cruising for the next ping, and repositioning. Default 35% is a
              typical urban figure — bump it up if you drive a lot between jobs.
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[var(--border)]">
              <button
                onClick={applyStatement}
                className="btn-primary"
                data-testid="rideshare-stmt-apply"
              >
                Fill calculator with these numbers <ChevronRight size={14} />
              </button>
              {importedAt && (
                <div
                  className="flex items-center gap-2 text-xs font-mono text-[var(--primary)]"
                  data-testid="rideshare-stmt-applied"
                >
                  <CheckCircle2 size={14} />
                  Applied · gross ${fmtNumber(Number(stmtIncome) || 0, 2)} over{" "}
                  {stmtWeeks} weeks → results updated below
                </div>
              )}
              {Number(stmtTrips) > 0 && Number(stmtIncome) > 0 && (
                <div className="ml-auto text-[11px] font-mono text-zinc-500">
                  Avg per trip: ${((Number(stmtIncome) || 0) / (Number(stmtTrips) || 1)).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---- Input + result grid ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 mt-10 border border-[var(--border)]">
        {/* Inputs */}
        <div className="lg:col-span-3 p-4 sm:p-6 md:p-8 border-r border-[var(--border)] space-y-6">
          {/* Day-job block */}
          <div>
            <div className="label-mono mb-3">Day job</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Annual salary / wages</div>
                <input
                  type="number"
                  step="1000"
                  className="input-sharp text-xl py-3"
                  value={mainIncome}
                  onChange={(e) => setMainIncome(e.target.value)}
                  data-testid="rideshare-main-income"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">
                  Alternative $/hr (optional)
                </div>
                <input
                  type="number"
                  step="1"
                  placeholder="e.g. overtime or freelance rate"
                  className="input-sharp text-xl py-3"
                  value={alternativeHourly}
                  onChange={(e) => setAlternativeHourly(e.target.value)}
                  data-testid="rideshare-alt-hourly"
                />
              </div>
            </div>
          </div>

          {/* Driving block */}
          <div>
            <div className="label-mono mb-3">Driving</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Platform</div>
                <select
                  className="input-sharp text-sm py-3"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  data-testid="rideshare-platform"
                >
                  {PLATFORM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">
                  Gross $/hr while driving
                </div>
                <input
                  type="number"
                  step="0.5"
                  className="input-sharp text-xl py-3"
                  value={hourlyGross}
                  onChange={(e) => setHourlyGross(e.target.value)}
                  data-testid="rideshare-hourly-gross"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Hours per week</div>
                <input
                  type="number"
                  step="1"
                  className="input-sharp text-xl py-3"
                  value={hoursPerWeek}
                  onChange={(e) => setHoursPerWeek(e.target.value)}
                  data-testid="rideshare-hours-week"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Weeks per year</div>
                <input
                  type="number"
                  step="1"
                  className="input-sharp text-xl py-3"
                  value={weeksPerYear}
                  onChange={(e) => setWeeksPerYear(e.target.value)}
                  data-testid="rideshare-weeks-year"
                />
              </div>
            </div>
          </div>

          {/* Vehicle block */}
          <div>
            <div className="label-mono mb-3">Vehicle</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Avg km per hour driving</div>
                <input
                  type="number"
                  step="1"
                  className="input-sharp text-xl py-3"
                  value={kmPerHour}
                  onChange={(e) => setKmPerHour(e.target.value)}
                  data-testid="rideshare-km-hour"
                />
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">Fuel type</div>
                <select
                  className="input-sharp text-sm py-3"
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  data-testid="rideshare-fuel"
                >
                  {FUEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2">
                  Total km/yr (all use, optional)
                </div>
                <input
                  type="number"
                  step="1000"
                  placeholder="0 = same as work km"
                  className="input-sharp text-xl py-3"
                  value={totalKmPerYear}
                  onChange={(e) => setTotalKmPerYear(e.target.value)}
                  data-testid="rideshare-total-km"
                />
              </div>
            </div>

            {/* Method toggle */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setExpenseMethod("ird")}
                className={
                  "text-xs font-mono px-3 py-2 border transition-colors " +
                  (expenseMethod === "ird"
                    ? "bg-[var(--primary)] text-black border-[var(--primary)]"
                    : "border-[var(--border)] text-zinc-400 hover:text-white")
                }
                data-testid="rideshare-method-ird"
              >
                IRD km rate (simple)
              </button>
              <button
                onClick={() => setExpenseMethod("actual")}
                className={
                  "text-xs font-mono px-3 py-2 border transition-colors " +
                  (expenseMethod === "actual"
                    ? "bg-[var(--primary)] text-black border-[var(--primary)]"
                    : "border-[var(--border)] text-zinc-400 hover:text-white")
                }
                data-testid="rideshare-method-actual"
              >
                Actual costs
              </button>

              <label className="ml-auto flex items-center gap-2 text-xs font-mono text-zinc-400">
                <input
                  type="checkbox"
                  checked={gstRegistered}
                  onChange={(e) => setGstRegistered(e.target.checked)}
                  data-testid="rideshare-gst-toggle"
                />
                GST registered
              </label>
            </div>

            {/* Progressive disclosure: actual-cost fields */}
            {expenseMethod === "actual" && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: "fuel", label: "Fuel / yr", v: actualFuel, set: setActualFuel },
                  { key: "insurance", label: "Insurance / yr", v: actualInsurance, set: setActualInsurance },
                  { key: "tyres", label: "Tyres / yr", v: actualTyres, set: setActualTyres },
                  { key: "servicing", label: "Servicing / yr", v: actualServicing, set: setActualServicing },
                  { key: "depreciation", label: "Depreciation / yr", v: actualDepreciation, set: setActualDepreciation },
                ].map(({ key, label, v, set }) => (
                  <div key={key}>
                    <div className="text-xs font-mono text-zinc-500 mb-1">{label}</div>
                    <input
                      type="number"
                      step="50"
                      className="input-sharp text-sm py-2"
                      value={v}
                      onChange={(e) => set(e.target.value)}
                      data-testid={`rideshare-actual-${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {expenseMethod === "ird" && (
              <div className="mt-4 text-[11px] font-mono text-zinc-500 leading-relaxed">
                Tier 1 <span className="text-[var(--primary)]">{fmtMoney(IRD_KM_RATES[fuelType].tier1)}/km</span> for
                the first {IRD_KM_TIER_THRESHOLD.toLocaleString()} km of total travel;
                Tier 2 <span className="text-[var(--primary)]">{fmtMoney(IRD_KM_RATES[fuelType].tier2)}/km</span> after.
                IRD rates for the {TAX_YEAR} income year.
              </div>
            )}
          </div>
        </div>

        {/* Result panel */}
        <div className="lg:col-span-2 p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Car size={16} className="text-[var(--primary)]" />
            <div className="label-mono">Your real hourly rate</div>
          </div>
          <div
            className="font-display text-5xl mt-3 text-[var(--primary)]"
            data-testid="rideshare-net-hourly"
          >
            {fmtMoney(r.netHourly)}<span className="text-lg text-zinc-500"> /hr</span>
          </div>
          <div className="text-xs font-mono text-zinc-500 mt-1">
            after tax, ACC, GST & vehicle costs
          </div>

          <div
            className={
              "mt-5 border-l-2 pl-3 text-sm font-mono leading-relaxed " +
              (verdict.tone === "warning"
                ? "border-[var(--error)] text-[var(--error)]"
                : verdict.tone === "success"
                  ? "border-[var(--primary)] text-white"
                  : "border-[var(--border)] text-zinc-300")
            }
            data-testid="rideshare-verdict"
          >
            {verdict.text}
          </div>

          <div className="mt-6 space-y-2 font-mono text-sm border-t border-[var(--border)] pt-4">
            <Row label="Gross fares (annual)" value={fmtMoney(r.grossDrivingIncome)} testid="rs-gross" />
            <Row label="Vehicle costs" value={`- ${fmtMoney(r.vehicleCosts)}`} />
            {r.gstNet > 0 && (
              <Row label="GST paid to IRD (net)" value={`- ${fmtMoney(r.gstNet)}`} />
            )}
            <Row label="Marginal income tax" value={`- ${fmtMoney(r.marginalTax)}`} />
            <Row label="ACC earner levy" value={`- ${fmtMoney(r.accLevy)}`} />
            <div className="border-t border-[var(--border)] pt-2">
              <Row
                label="Net in pocket / yr"
                value={fmtMoney(r.netInPocket)}
                accent
                big
                testid="rs-net-annual"
              />
            </div>
          </div>

          <div className="hi-vis-stripe h-1.5 mt-8 -mx-4 sm:-mx-6 md:-mx-8 -mb-4 sm:-mb-6 md:-mb-8" />
        </div>
      </div>

      {/* ---- Comparison bar ---- */}
      <div className="mt-8 border border-[var(--border)] p-5 sm:p-6" data-testid="rideshare-comparison">
        <div className="label-mono mb-4">Side-by-side (net $/hr)</div>
        <ComparisonBar
          rows={[
            { label: "Driving (this calc)", value: r.netHourly, accent: true },
            r.altHourly > 0 && r.altHourly !== r.mainHourly
              ? { label: "Your alternative", value: r.altHourly }
              : null,
            { label: "Main job (net, ~2080h)", value: r.mainHourly },
            { label: `NZ minimum wage (${TAX_YEAR})`, value: MIN_WAGE_HOURLY },
          ].filter(Boolean)}
        />
      </div>

      {/* ---- Sensitivity + breakdown ---- */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-[var(--border)] p-5 sm:p-6" data-testid="rideshare-sensitivity">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={14} className="text-[var(--error)]" />
            <div className="label-mono">If costs jump 15–20%</div>
          </div>
          <div className="font-mono text-sm text-zinc-300 leading-relaxed">
            Slower traffic, a fuel-price rise, or an unexpected service could drop your rate to{" "}
            <span className="text-[var(--error)] font-bold">
              {fmtMoney(r.netHourlyIfFuelUp)}/hr
            </span>
            . That&apos;s how sensitive the number is.
          </div>
          <div className="mt-3 text-[11px] font-mono text-zinc-500">
            Break-even gross rate ≈{" "}
            {fmtMoney(
              r.vehiclePerHour +
                (r.marginalRatePct / 100) * Math.max(0, r.hourlyGross - r.vehiclePerHour)
            )}
            /hr — below this you&apos;re paying to drive.
          </div>
        </div>

        <div className="border border-[var(--border)] p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Info size={14} className="text-[var(--primary)]" />
            <div className="label-mono">Where the money goes (annual)</div>
          </div>
          <MoneyFlow
            gross={r.grossDrivingIncome}
            vehicle={r.vehicleCosts}
            gst={r.gstNet}
            tax={r.marginalTax}
            acc={r.accLevy}
            net={r.netInPocket}
          />
        </div>
      </div>

      {/* ---- GST flat-rate context box ---- */}
      {!gstRegistered && (
        <div className="mt-8 border border-[var(--border)] p-5 sm:p-6" data-testid="rideshare-gst-context">
          <div className="label-mono mb-2">GST flat-rate credit (non-registered)</div>
          <div className="text-sm font-mono text-zinc-300 leading-relaxed">
            Uber remits{" "}
            <span className="text-[var(--primary)]">
              {(GIG_FLAT_RATE.ird_share * 100).toFixed(1)}%
            </span>{" "}
            GST to IRD for you, and credits{" "}
            <span className="text-[var(--primary)]">
              {(GIG_FLAT_RATE.driver_credit * 100).toFixed(1)}%
            </span>{" "}
            back on top of your fare — this is <em>not</em> a cost, it&apos;s already
            baked into what Uber pays you. That&apos;s why we don&apos;t deduct any GST above.
          </div>
          <div className="mt-3 text-xs font-mono text-zinc-500 leading-relaxed">
            Once your gig turnover crosses $60,000 in a 12-month window you must
            register for GST — at that point the maths on this page changes.
          </div>
        </div>
      )}

      {/* ---- Disclaimer ---- */}
      <div className="mt-8 text-[11px] font-mono text-zinc-500 max-w-3xl leading-relaxed">
        General estimate only, not personalised tax advice. Actual results
        depend on your vehicle, IRD kilometre-method election vs actual costs,
        ACC classification, and GST status. KiwiSaver, student-loan repayments
        and Working for Families entitlements may also change the picture — if
        those apply to you, get a proper review.
      </div>

      {/* ---- Contextual nudges ---- */}
      <NudgeCard
        show={r.hourlyGross > 0 && r.netHourly < r.minWage}
        tone="warning"
        title="Your true rate is below minimum wage"
        body="If you'd earn more picking up an extra shift or a second job at the standard rate, the maths says do that instead. If driving flexibility matters to you, that's fine — just go in eyes open."
        cta="Talk to a tax agent"
        campaign="rideshare-below-minwage"
      />

      <NudgeCard
        show={r.grossDrivingIncome > 60000}
        tone="info"
        title="You're near or over the GST threshold"
        body="Once your driving turnover hits $60k in any 12-month period you must register for GST. Getting the timing and method right is where most drivers overpay."
        cta="Get GST sorted"
        campaign="rideshare-gst-threshold"
      />

      <EmailResultsCard
        calculator="rideshare"
        payload={{
          platform,
          main_income: Number(mainIncome) || 0,
          hourly_gross: Number(hourlyGross) || 0,
          hours_per_week: Number(hoursPerWeek) || 0,
          weeks_per_year: Number(weeksPerYear) || 0,
          annual_hours: r.annualHours,
          gross_annual: r.grossDrivingIncome,
          vehicle_costs: r.vehicleCosts,
          marginal_tax: r.marginalTax,
          acc_levy: r.accLevy,
          gst_net: r.gstNet,
          net_annual: r.netInPocket,
          net_hourly: r.netHourly,
          method: expenseMethod,
          fuel_type: fuelType,
          gst_registered: gstRegistered,
        }}
        labelKey="email.rideshare.label"
        descKey="email.rideshare.desc"
      />

      <HireUsCTA
        campaign="rideshare-calc"
        headline="Driving as a side hustle? Get the tax right."
        subline="We handle the IR3, vehicle logbook, and marginal-rate maths for gig drivers every day. One call and you'll know exactly what to set aside."
      />
    </div>
  );
}

// ---------- Small subcomponents ----------------------------------------
function Row({ label, value, big, accent, testid }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="label-mono">{label}</span>
      <span
        data-testid={testid}
        className={`${big ? "text-2xl" : "text-base"} ${
          accent ? "text-[var(--primary)]" : "text-white"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function ComparisonBar({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(0, r.value)));
  return (
    <div className="space-y-3">
      {rows.map((row, i) => {
        const w = Math.max(2, (Math.max(0, row.value) / max) * 100);
        return (
          <div key={i}>
            <div className="flex items-baseline justify-between text-xs font-mono mb-1">
              <span className={row.accent ? "text-[var(--primary)]" : "text-zinc-400"}>
                {row.label}
              </span>
              <span className={row.accent ? "text-white font-bold" : "text-zinc-300"}>
                {fmtMoney(row.value)}/hr
              </span>
            </div>
            <div className="h-2 bg-[var(--border)] relative overflow-hidden">
              <div
                className={row.accent ? "h-2 bg-[var(--primary)]" : "h-2 bg-zinc-500"}
                style={{ width: `${w}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MoneyFlow({ gross, vehicle, gst, tax, acc, net }) {
  const items = [
    { label: "Gross fares", value: gross, tone: "muted" },
    { label: "Vehicle", value: -vehicle, tone: "cost" },
    gst > 0 ? { label: "GST to IRD", value: -gst, tone: "cost" } : null,
    { label: "Income tax", value: -tax, tone: "cost" },
    { label: "ACC levy", value: -acc, tone: "cost" },
    { label: "Net in pocket", value: net, tone: "net" },
  ].filter(Boolean);
  return (
    <div className="space-y-2 font-mono text-sm">
      {items.map((it, i) => (
        <div key={i} className="flex items-baseline justify-between">
          <span
            className={
              it.tone === "net"
                ? "text-[var(--primary)] font-bold"
                : it.tone === "cost"
                  ? "text-zinc-400"
                  : "text-zinc-300"
            }
          >
            {it.label}
          </span>
          <span
            className={
              it.tone === "net"
                ? "text-[var(--primary)] font-bold text-lg"
                : it.tone === "cost"
                  ? "text-[var(--error)]"
                  : "text-white"
            }
          >
            {fmtMoney(it.value)}
          </span>
        </div>
      ))}
      <div className="text-[10px] text-zinc-500 mt-2">
        Also mentally set aside {fmtNumber(Math.max(0, ((tax + acc) / Math.max(1, gross)) * 100), 1)}% of
        each fare for tax + ACC.
      </div>
    </div>
  );
}
